import { useEffect } from 'react';
import { useGlobalStore } from '../../stores/global-store';
import { useLoaderStore } from '../../stores/loader-store';
import { useView } from '../view/use-view';
import ResultSkeleton from '../../shared/components/ResultSkeleton';
import ErrorBoundary from '../../shared/components/ErrorBoundary';
import { PIXELATE_IN_OUT_ENTER_CLASS } from '../../animations/pixelateInOut';
import ResultItem from './ResultItem';
import { useMp3PreloadWindow } from './useMp3PreloadWindow';

// Port of the results-grid portion of src/app/components/main-content/
// main-content.component.html (the #scrollTarget grid: skeleton loop, songs
// loop incl. the "Similar Songs" divider rows, and the no-more-results alert)
// plus the infinite-scroll trigger.
//
// Infinite scroll: in Angular this lived in app.component.ts — after a 3s
// startup delay it subscribed to 'scroll' on #results-scroll-container
// (throttleTime(100), leading edge) and, once the user passed half the content
// height, called loadMore(), which sets globalState.loadMoreSongsEvent = true
// when nothing is loading and there are more results. The nav-bar reacts to
// loadMoreSongsEvent by fetching the next page. Ported here 1:1 (the scroll
// container itself is rendered by MainContent; coordination happens only via
// the global store's loadMoreSongsEvent field).
//
// Per-item host decorations from the Angular template ([attr.data-result-item],
// class="pop-animate max-w-[400px]", isInView) are rendered by ResultItem
// itself (React components have no host element) — see ResultItem.tsx.
//
// getSanitizedMessage() used bypassSecurityTrustHtml (render the message
// verbatim) → dangerouslySetInnerHTML.

const SKELETON_COUNT = 30;

function loadMore() {
  const globalState = useGlobalStore.getState();
  if (!globalState.songsLoading && !useLoaderStore.getState().isLoading && !globalState.showNoMoreResults) {
    globalState.setGlobalField('loadMoreSongsEvent', true);
  }
}

export default function ResultsGrid() {
  const view = useView();
  const songs = useGlobalStore(s => s.songs);
  const showNoMoreResults = useGlobalStore(s => s.showNoMoreResults);
  const noMoreResultsMessage = useGlobalStore(s => s.noMoreResultsMessage);

  // WL MP3 preload window: ids of the ~15 visible MP3 cards to preload. Matching
  // is by song id (not list index), so the "Similar Songs" divider rows — which
  // occupy a slot in `songs` but render no ResultItem and carry id '' — are
  // harmlessly ignored here.
  const preloadWindowIds = useMp3PreloadWindow(songs);

  // Infinite scroll wiring (port of AppComponent.ngAfterViewInit, incl. the 3s
  // delay before the scroll container is looked up).
  useEffect(() => {
    let scrollContainer: HTMLElement | null = null;
    let throttled = false;
    let throttleTimer: ReturnType<typeof setTimeout> | undefined;

    const onScroll = () => {
      // rxjs throttleTime(100): leading-edge — handle the first event, then
      // ignore the rest of the 100ms window.
      if (throttled) {
        return;
      }
      throttled = true;
      throttleTimer = setTimeout(() => {
        throttled = false;
      }, 100);

      // Check if the user has scrolled to at least half the content height
      if (scrollContainer
        && (scrollContainer.scrollTop + scrollContainer.clientHeight) >= (scrollContainer.scrollHeight * 0.5)) {
        loadMore();
      }
    };

    const startupTimer = setTimeout(() => {
      // Get the scroll container element
      scrollContainer = document.getElementById('results-scroll-container');
      scrollContainer?.addEventListener('scroll', onScroll);
    }, 3000);

    return () => {
      clearTimeout(startupTimer);
      clearTimeout(throttleTimer);
      scrollContainer?.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <div className="p-2 mt-2 sm:mt-0 md:p-4 w-full h-full grid gap-4 grid-cols-fit-sm sm:grid-cols-fit-lg"
         id="scrollTarget">
      {/* Show Skeleton */}
      {view !== 'home' && view !== 'curated' && !showNoMoreResults && songs.length === 0 && (
        Array.from({ length: SKELETON_COUNT }, (_, index) => (
          <div key={index} className={`max-w-[400px] ${PIXELATE_IN_OUT_ENTER_CLASS}`}>
            <ResultSkeleton />
          </div>
        ))
      )}
      {view !== 'home' && songs.length > 0 && (
        songs.map((song, index) =>
          song.showEmptyLine ? (
            <div key={song.id} className="col-span-full flex items-center border-gray-300 my-4">
              <span className="flex-grow border-t border-gray-300"></span>
              <span className="mx-4 text-gray-500">Similar Songs</span>
              <span className="flex-grow border-t border-gray-300"></span>
            </div>
          ) : (
            // Per-card boundary: a single malformed song can't take down the
            // whole grid — it just omits that card (fallback={null}).
            <ErrorBoundary key={song.id} name="result-item" fallback={null}>
              <ResultItem
                  isFirst={index === 0}
                  shouldPrioritize={index < 25}
                  songPositionInList={index}
                  preloadAudio={preloadWindowIds.has(song.id)}
                  song={song} />
            </ErrorBoundary>
          )
        )
      )}
      {showNoMoreResults && view !== 'home' && (
        <div className="h-full w-[100vw] flex justify-center items-end p-4 z-10">
          <div
              className="flex items-center p-6 rounded-xl bg-mypart_red-500/10 dark:bg-mypart_red-300/20 backdrop-blur-sm mb-4 shadow-lg max-w-[90vw] border border-mypart_red-300/30 dark:border-mypart_red-300/50"
              role="alert">
            <svg aria-hidden="true" className="flex-shrink-0 w-6 h-6 text-mypart_red-500 dark:text-mypart_red-300"
                 fill="currentColor"
                 viewBox="0 0 20 20"
                 xmlns="http://www.w3.org/2000/svg">
              <path
                  d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"/>
            </svg>
            <span className="sr-only">Info</span>
            <div dangerouslySetInnerHTML={{ __html: noMoreResultsMessage }}
                 className="ml-4 text-base lg:text-lg font-medium text-center lg:text-left text-gray-800 dark:text-gray-100">
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
